"use client";
import React from "react";
import Image from "next/image";
import { Box, Divider, SxProps, Theme, Typography } from "@mui/material";

import DateIcon from "@mui/icons-material/TodayRounded";
import LocationIcon from "@mui/icons-material/FmdGoodOutlined";
import ShipIcon from "@mui/icons-material/DirectionsBoatFilledRounded";

import { ICruise } from "@/interfaces/IAllCruisesList";
import { convertToDateV1 } from "@/helpers/helpers";
import { toKebabCase } from "@/utils/helpers";
import LinkButton from "../common/LinkButton";

const CruiseSummaryCard: React.FC<{
  cruise: ICruise;
  lowestPriceInfo: {
    price: string;
    type: string;
    displayName: string;
  };
}> = ({ cruise, lowestPriceInfo }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        bgcolor: "#EDF1FF",
        width: "100%",
        borderRadius: "15px",
        transition: "all 0.5s",
        p: "15px",
        overflow: "hidden",
        border: "1px solid #C1BEFFaa",
        boxShadow: { xs: "0px 0px 0px 1px #C1BEFF, 0px 0px 15px #C1BEFFaa", md: "unset" },
        "&:hover": {
          boxShadow: {
            xs: "0px 0px 0px 1px #C1BEFF, 0px 0px 15px #C1BEFFaa",
            md: "0px 0px 0px 1px #C1BEFF, 0px 0px 15px #C1BEFFaa",
          },
          border: { xs: "1px solid #C1BEFFaa", md: "1px solid #C1BEFF" },
        },
      }}
    >
      <Box sx={{}}>
        <Typography
          variant="h3"
          sx={{
            mb: "8px",
            mt: "-2px",
            fontSize: { xs: "22px", md: "20px" },
            color: "primary.main",
          }}
        >
          {cruise?.name}{" "}
          <Box
            component="span"
            sx={{
              bgcolor: "#FFBB00",
              color: "#350097",
              fontSize: "12px",
              p: "2px 5px 2px 4px",
              textTransform: "uppercase",
              borderRadius: "4px",
              border: "1px solid #5900FF80",
              ml: "0px",
              position: "relative",
              top: "-3px",
            }}
          >
            {cruise?.cruiseNights} Nights
          </Box>
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "14px",
          }}
        >
          <Box>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: { xs: "160px", md: "160px" },
                borderRadius: "7px",
                overflow: "hidden",
                img: {
                  width: "100%",
                  height: { xs: "160px", md: "160px" },
                  objectFit: "cover",
                  display: "flex",
                  borderRadius: "7px",
                },
              }}
            >
              {cruise?.shipImageHref && (
                <Image
                  src={cruise?.shipImageHref}
                  alt={cruise?.shipTitle}
                  width={400}
                  height={250}
                  unoptimized
                  className="panning_image"
                />
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "1fr" },
              "> div:nth-of-type(3)": {
                gridColumn: { xs: "1/4", md: "unset" },
              },
            }}
          >
            <CardLabelUI icon={<DateIcon />} title="Embarking From" label={cruise?.startsAt} />

            <CardLabelUI
              icon={<LocationIcon />}
              title="Embarking On"
              label={convertToDateV1(cruise?.startsOn)}
            />

            <CardLabelUI
              icon={<ShipIcon />}
              title="Sailing On"
              label={
                <>
                  {cruise?.shipTitle} by <span>{cruise?.operatorTitle}</span>
                </>
              }
            />
          </Box>
        </Box>

        {cruise?.description !== "" && (
          <>
            <Divider />
            <Box
              sx={{
                fontSize: "14px",
                mt: "10px",
                mb: "10px",
                color: "#444",
                fontWeight: 400,
                overflow: "hidden",
                WebkitLineClamp: { xs: 3, sm: 4 },
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                textOverflow: "ellipsis",
                lineHeight: 1.45,
              }}
              dangerouslySetInnerHTML={{ __html: cruise?.description }}
            ></Box>
            <Divider sx={{ mb: "-11px" }} />
          </>
        )}
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{}}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              width: "100%",
              gap: "10px",
              mt: "10px",
            }}
          >
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  mt: "8px",
                  lineHeight: 1.2,
                }}
              >
                PRICE FROM
              </Box>
              <Typography
                variant="body1"
                sx={{
                  mt: "-3px",
                  color: "#E30053",
                  fontSize: "25px",
                  lineHeight: 1.1,
                  fontWeight: 600,
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  span: {
                    color: "#A8215C",
                    fontSize: "22px",
                  },
                  ".gbp": {
                    mr: "2px",
                  },
                  ".pp": {
                    ml: "2px",
                  },
                }}
              >
                <span className="gbp">£</span>
                {lowestPriceInfo.price}
                <span className="pp">pp</span>

                {lowestPriceInfo && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 400,
                      position: "absolute",
                      top: "9px",
                      left: "95px",
                      width: "max-content",
                      color: "#060273",
                    }}
                  >
                    {lowestPriceInfo.displayName}
                  </Box>
                )}
              </Typography>
            </Box>

            <LinkButton
              href={`/cruise-results/${toKebabCase(cruise?.name ?? "")}?cruiseRef=${
                cruise?.ref
              }&shipRef=${cruise?.ship?.split("/").pop()?.split(".")[0] ?? ""}`}
              sx={{}}
            >
              View Itinerary
            </LinkButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CruiseSummaryCard;

const CardLabelUI: React.FC<{
  icon: React.ReactNode;
  title: string;
  label: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ icon, title, label, sx }) => {
  return (
    <Box sx={{ mb: "12px", ...sx }}>
      <Box
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "#7D46C9",
          ml: "-3px",
          mb: "2px",
          display: "flex",
          gap: "4px",
          alignItems: "center",
          svg: {
            width: "20px",
            height: "20px",
            mb: "-2px",
          },
        }}
      >
        <Box>{icon}</Box>
        <Box>{title}</Box>
      </Box>
      <Box
        sx={{
          fontSize: "14px",
          fontWeight: 400,
          color: "#222",
          mb: "2px",
          span: {
            fontWeight: 600,
          },
        }}
      >
        {label}
      </Box>
    </Box>
  );
};
